import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { QrCode, Download, Copy, ExternalLink, Heart } from "lucide-react";
import api from "@/lib/api";

/**
 * NonprofitQRCard — shown on the nonprofit's Give Back dashboard. Renders the
 * QR code that resolves to their public AddVendTour profile (`/vendors/{slug}`),
 * which itself shows the nonprofit's mission, running donation total, and a
 * one-tap Donate button — plus links back to Discover Events and the wider
 * Give Back directory so donors can keep exploring.
 *
 * Backed by the SAME endpoint used for vendor QRs — nonprofits are users
 * with slugs on the shared `/vendors/{slug}` public route:
 *   GET /api/vendors/by-slug/{slug}/qr.png   (image bytes, 24h ETag cache)
 *   GET /api/vendors/by-slug/{slug}/qr       (JSON — the canonical URL)
 *
 * Actions:
 *   - Preview the code inline
 *   - Copy the profile URL (share as a plain link)
 *   - Download the PNG (for donation-station signs, event booths, mailers)
 *   - Open the public page in a new tab to verify what donors will see
 */
export default function NonprofitQRCard({ slug, orgName }) {
  const [busy, setBusy] = useState(false);
  // Canonical URL comes from the backend so the UI reflects the truth of what
  // the QR actually encodes (FRONTEND_URL origin, NOT the browser origin).
  const [canonicalProfileUrl, setCanonicalProfileUrl] = useState("");
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    api.get(`/vendors/by-slug/${slug}/qr`)
      .then((r) => { if (alive) setCanonicalProfileUrl(r.data?.profile_url || ""); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  if (!slug) {
    return (
      <div className="card-warm rounded-2xl p-5" data-testid="np-qr-card-empty">
        <div className="flex items-center gap-2">
          <QrCode className="h-4 w-4" style={{ color: "var(--avt-pink)" }} />
          <div className="overline" style={{ color: "var(--avt-pink)" }}>Your donor QR</div>
        </div>
        <p className="text-sm mt-2" style={{ color: "var(--ink-soft)" }}>
          Save your profile at least once to generate a shareable QR code donors can scan to give.
        </p>
      </div>
    );
  }

  const backendOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  // v=2 busts any earlier caches from before the FRONTEND_URL fix.
  const qrUrl = `${backendOrigin}/api/vendors/by-slug/${slug}/qr.png?v=2`;
  const profileUrl = canonicalProfileUrl || `${window.location.origin}/vendors/${slug}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      toast.success("Donate link copied");
    } catch { toast.error("Copy failed"); }
  };

  const downloadPng = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/vendors/by-slug/${slug}/qr.png?v=2`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `addvendtour-donate-${slug}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("QR code downloaded");
    } catch {
      toast.error("Download failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="card-warm rounded-2xl p-6"
      data-testid="np-qr-card"
      style={{ borderColor: "var(--avt-pink)" }}
    >
      <div className="flex items-center gap-2">
        <Heart className="h-4 w-4" style={{ color: "var(--avt-pink)" }} fill="var(--avt-pink)" strokeWidth={0} />
        <div className="overline" style={{ color: "var(--avt-pink)" }}>Your donor QR</div>
      </div>
      <h3 className="font-serif text-2xl mt-1">Get scanned. Get supported.</h3>
      <p className="text-sm mt-1 mb-4" style={{ color: "var(--ink-soft)" }}>
        Print this on donation-station signs, event tables, mailers, or thank-you cards.
        One scan takes donors straight to <strong>{orgName || "your"}</strong> profile — where they
        can learn your story, donate in a tap, and browse more events + orgs on AddVendTour.
      </p>

      <div className="flex items-start gap-4 flex-wrap">
        <div
          className="p-3 rounded-sm flex-shrink-0"
          style={{ backgroundColor: "var(--paper-warm)", border: "1px solid var(--sand)" }}
        >
          <img
            src={qrUrl}
            alt={`Donate QR code for ${orgName || slug}`}
            width={200}
            height={200}
            className="block"
            data-testid="np-qr-img"
            onError={(e) => {
              e.currentTarget.style.opacity = "0.3";
              e.currentTarget.setAttribute("title", "QR temporarily unavailable — refresh in a moment.");
            }}
          />
        </div>

        <div className="flex-1 min-w-[220px] space-y-2">
          <div className="text-xs" style={{ color: "var(--ink-soft)" }}>Scans to:</div>
          <div
            className="text-sm font-mono truncate p-2 rounded-sm"
            style={{ backgroundColor: "var(--sand)", color: "var(--ink)" }}
            data-testid="np-qr-target-url"
          >
            {profileUrl}
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              type="button"
              size="sm"
              onClick={downloadPng}
              disabled={busy}
              data-testid="np-qr-download"
              className="btn-primary rounded-full"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {busy ? "Downloading…" : "Download PNG"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={copyLink}
              data-testid="np-qr-copy-link"
              className="rounded-full"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
            </Button>
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="np-qr-open"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border"
              style={{ borderColor: "var(--border-warm)", color: "var(--ink)" }}
            >
              <ExternalLink className="h-3.5 w-3.5" /> Preview
            </a>
          </div>
          <p className="text-[11px] pt-1" style={{ color: "var(--ink-soft)" }}>
            Prints crisply at up to 4&times;4&nbsp;inches — plenty of room on event
            table cards, thank-you notes, or a full-page collection sign.
          </p>
        </div>
      </div>
    </div>
  );
}
